import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {NolimitPlugin} from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import {GameModelEvent} from "@nolimitcity/slot-game/bin/model/GameModel";
import {Logger, LogLevel} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {GameFlowController} from "@nolimitcity/slot-game/bin/flowcontrol/GameFlowController";
import {GameModeNames} from "./server/GameModeNames";
import {FlowState} from "@nolimitcity/slot-game/bin/flowcontrol/FlowState";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FontLoader} from "@nolimitcity/slot-launcher/bin/loader/FontLoader";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {AssetsConfig} from "@nolimitcity/slot-game/bin/asset/AssetsConfig";
import {AssetLoader} from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/asset/ResourcesGroupName";
import {GameAssets} from "./GameAssets";
import {Sound} from "./sound/Sound";
import {BackgroundElements} from "./view/BackgroundElements";
import {WinPresentation} from "./gameflow/WinPresentation";
import {NormalBetPresentation} from "./gameflow/NormalBetPresentation";
import {NormalResultPresentation} from "./gameflow/NormalResultPresentation";
import {ZeroBetIdlePresentation} from "./gameflow/ZeroBetIdlePresentation";
import {
    IFeatureData,
    IGameData,
    IHandleCorner,
    IMapStrToStr,
    IMonster,
    IMonsterNameToIdx,
    IMonsterWalk,
    IShakeConfig,
    ISymbolValues,
    MONSTER_WEAPON_ACTIONS,
    WILD_SKIN_NAMES
} from "./server/IData";
import {Model} from "./server/Model";
import {ReelsView} from "./reels/view/ReelsView";
import {ReelCorners} from "./view/ReelCorners";
import {SymbolFloors} from "./reels/view/SymbolFloors";
import {Speedster} from "./debug/Speedster";
import {SpinePreviewer} from "./debug/SpinePreviewer";
import {AvalancheFillUpPresentation} from "./gameflow/AvalancheFillUpPresentation";
import {MonsterReelsView} from "./reels/view/MonsterReelsView";
import {CountUp} from "./view/CountUp";
import {Wall} from "./view/Wall";
import {OverlaysForSymbols} from "./view/OverlaysForSymbols";
import {Multiplier} from "./view/Multiplier";
import {FeatureOutro} from "./view/FeatureOutro";
import {ScatterInventory} from "./view/ScatterInventory";
import {GEStates, Symbol, SymbolStates, WildStates, XBombStates} from "./reels/Symbol";
import {CollectAreaHighlightView} from "./reels/view/CollectAreaHighlightView";
import {gsap} from "gsap";
import {ReelModel} from "./reels/ReelModel";
import {BlackBg} from "./view/BlackBg";
import {BigWin} from "./view/BigWin";
import {TemplateAsset, TemplateLoader} from "@nolimitcity/slot-launcher/bin/loader/TemplateLoader";
import {NormalBetIdlePresentation} from "./gameflow/NormalBetIdlePresentation";
import {MaxWinCelebration} from "./view/MaxWinCelebration";
import {FeatureIntro} from "./view/FeatureIntro";
import {GameLogo} from "./view/GameLogo";
import {
    BonusFeatureDisplayConfigurationList
} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";
import {BonusBuyPopUpContent} from "@nolimitcity/promo-panel/bin/feature/generic/BonusBuyPopUpContent";
import {Label} from "@nolimitcity/slot-launcher/bin/gui/labels/Label";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {MonsterSymbol} from "./reels/MonsterSymbol";
import {Skinner} from "@nolimitcity/slot-launcher/bin/utils/Skinner";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class Game extends SlotGame {

    // these are constants.
    public static TOTAL_REELS_NUM: number = 9;
    public static DEFAULT_REELS_NUM: number = 6;
    public static DEFAULT_ROWS_NUM: number = 6;
    public static DEFAULT_SYMBOL_WIDTH: number = 110;
    public static DEFAULT_SYMBOL_HEIGHT: number = 100;
    public static REEL_AREA_X: number = 90;
    public static REEL_AREA_Y: number = 135;
    public static LIFE_COUNT: number = 3;
    public static MAYHEM_LIFE_COUNT: number = 1;
    public static ORDINARY_PAYOUT_SYMBOLS: string[] = ['P1', 'P2', 'P3', 'P4'];
    public static PAYOUT_SYMBOLS: string[] = this.ORDINARY_PAYOUT_SYMBOLS.concat(["W", "XS", "XN"]); // XS(xSplit) and XN(xNudge) both converts to wild before monster walks.
    public static MONSTER_SYMBOLS: string[] = ["C1", "C2", "C3", "C4"];
    public static BOOSTER_SYMBOLS: string[] = ["AS", "BU", "BZ", "GR"];
    public static MONSTER_NAME_TO_IDX: IMonsterNameToIdx = {
        "C1": 0,
        "C2": 1,
        "C3": 2,
        "C4": 3
    };
    public static MONSTER_NAME_TO_COLOR: IMapStrToStr = {
        "C1": "RED",
        "C2": "GREEN",
        "C3": "CYAN",
        "C4": "BLUE"
    };
    public static BIG_WIN_LVL_TO_SOUND: IMapStrToStr = {
        "BIG_WIN_01": "BIG_WIN_LEVEL1",
        "BIG_WIN_02": "BIG_WIN_LEVEL2",
        "BIG_WIN_03": "BIG_WIN_LEVEL3",
        "BIG_WIN_04": "BIG_WIN_LEVEL4",
        "BIG_WIN_05": "BIG_WIN_LEVEL5"
    };

    // these aren't constants.
    public static ACTIVE_REELS_NUM: number = 6;
    public static ACTIVE_ROWS_NUM: number = 6;
    public static SYMBOL_WIDTH: number = 110;
    public static SYMBOL_HEIGHT: number = 100;
    public static MONSTERS_HURT_STATUS: boolean[] = [false, false, false, false];
    public static visibleMonstersCount: number = this.MONSTER_SYMBOLS.length;
    public static scaleFactor: number = 1;
    public static hasGridExpanded: boolean;
    public static gridSize: string = "6by6";
    public static noOfScatterLanded: number = 0;
    public static noOfWildLanded: number = 0;
    public static monsterCarriedSymbols: string[][] = [[], [], [], []];
    public static monsterExtraCarriedSymbols: string[][] = [[], [], [], []];
    public static maxExpFeatureSymPos: string[] = [];
    public static model: Model;
    public static assetLoader: AssetLoader;
    public static soundPlayer: Sound;
    public static backgroundElements: BackgroundElements;
    public static gameLogo: GameLogo;
    public static gameFlowController: GameFlowController;
    public static reelsView: ReelsView;
    public static reelCorners: ReelCorners;
    public static monsterReelsView: MonsterReelsView;
    public static collectAreaHighlightView: CollectAreaHighlightView;
    public static symbolFloors: SymbolFloors;
    public static countUp: CountUp;
    public static wall: Wall;
    public static overlaysForSymbols: OverlaysForSymbols;
    public static multiplier: Multiplier;
    public static featureIntro: FeatureIntro;
    public static featureOutro: FeatureOutro;
    public static scatterInventory: ScatterInventory;
    public static bigWin: BigWin;
    public static blackBg: BlackBg;
    public static mxCelebration: MaxWinCelebration;
    public static mxSymbol: Symbol | null;
    public static mxVSymbolReelId: number;
    public static mxVSymbolRowIdBeforeCollapse: number;
    public static mxVSymbolRowIdAfterCollapse: number;
    public static hasMxSymbolLanded: boolean = false;
    public static isSFSActive: boolean = false;
    public static normalIdle: NormalBetIdlePresentation;
    public static monsterSymbolsGoingDead: Symbol[] = [];
    public static monstersToDieAfterAvalanche: string[] = [];
    public static isPlayMonsterDied: boolean = true;
    public static isPlayXLootExplode: boolean = true;
    public static isPlayHeartRestore: boolean = true;
    public static isPlayPickupHighlight: boolean = true;
    public static surprisedMonsters: string[] = [];

    private _defaultSymbolValues: ISymbolValues = {
        "P1": {"1": 0.15},
        "P2": {"1": 0.10},
        "P3": {"1": 0.05},
        "P4": {"1": 0.05}
    };

    private skinner: Skinner;

    constructor() {
        super();
        const validSkins: string[] = [
            "sweep"
        ]
        this.skinner = new Skinner(validSkins);
    }

    private handleSweepInit() {
        //Making sure that skinID is set based  on "replaceWordFree" gcc.
        if (SlotGame.apiPlugin.gameClientConfiguration.isSet) {
            if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                this.skinner.skinId = "sweep";
            }
        } else {
            SlotGame.apiPlugin.events.on(APIEvent.GAME_CLIENT_CONFIGURATION_APPLIED, () => {
                if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                    this.skinner.skinId = "sweep";
                }
            })
        }
    }

    private handleSweepGetReady(): Promise<any> {
        if (this.skinner.skinId != undefined) {
            const replaceMap: Map<string, string> = new Map();
            replaceMap.set("BOOSTED_BET", "BOOSTED_BET.png");
            return this.skinner.loadSkin(replaceMap);
        }
        return Promise.resolve();
    }

    private static getGridContainerOffset(gridName: string): number[] {
        switch (gridName) {
            case "8by6":
                return [-3, 80];
            case "6by8":
                return [82, -20];
            case "8by8":
                return [-3, -20];
            case "9by8":
                return [7, 20];
            case "8by9":
                return [41, -20];
            case "9by9":
                return [2, -20];
            default:
                return [-3, 0];
        }
    }

    private static resizeReelsOnStart(): void {
        const reelsData: string[][] = Game.model.initData.reelsAfterAvalanche;

        this.getReelGridSize(reelsData);
        const addReels: boolean = Game.ACTIVE_REELS_NUM !== Game.DEFAULT_REELS_NUM,
            addRows: boolean = Game.ACTIVE_ROWS_NUM !== Game.DEFAULT_ROWS_NUM;

        if (!addReels && !addRows) {
            return;
        }

        Game.hasGridExpanded = true;

        if ((Game.ACTIVE_REELS_NUM === (Game.TOTAL_REELS_NUM)) || (Game.ACTIVE_ROWS_NUM === (Game.TOTAL_REELS_NUM))) {
            Game.scaleFactor = 0.7;
        } else if ((Game.ACTIVE_REELS_NUM === (Game.TOTAL_REELS_NUM - 1)) || (Game.ACTIVE_ROWS_NUM === (Game.TOTAL_REELS_NUM - 1))) {
            Game.scaleFactor = 0.8;
        }

        Game.SYMBOL_WIDTH = (Game.scaleFactor * Game.DEFAULT_SYMBOL_WIDTH);
        Game.SYMBOL_HEIGHT = (Game.scaleFactor * Game.DEFAULT_SYMBOL_HEIGHT);
    }

    private static shake(config: IShakeConfig): gsap.core.Timeline {
        let startPosY: number = config.obj ? config.obj.y : 0;
        let startPosX: number = config.obj ? config.obj.x : 0;

        let t: boolean = true;
        let defaultX: number = config.obj ? config.obj.x : 0, defaultY: number = config.obj ? config.obj.y : 0;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                startPosY = config.obj.y;
                startPosX = config.obj.x;
            },
            onUpdate: () => {
                const nt: number = tl.progress();
                const lt: number = tl.time();
                let et: number = nt;
                if (t) {
                    t = false;
                    return;
                }

                if (config.intensity) {
                    if (config.y != 0 && config.obj && config.obj.y) {
                        if (Number.isInteger(config.obj.y)) {
                            defaultY = config.obj.y;
                        }
                        config.obj.y = startPosY + Math.cos(lt * config.hertz) * (config.intensity * et) * config.y;
                    }
                    if (config.x != 0 && config.obj && config.obj.x) {
                        if (Number.isInteger(config.obj.x)) {
                            defaultX = config.obj.x;
                        }
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * (config.intensity * et)) * config.x;
                    }
                } else {
                    if (config.y != 0 && config.obj && config.obj.y) {
                        if (Number.isInteger(config.obj.y)) {
                            defaultY = config.obj.y;
                        }
                        config.obj.y = startPosY + (Math.cos(lt * config.hertz) * et) * config.y;
                    }
                    if (config.x != 0 && config.obj && config.obj.x) {
                        if (Number.isInteger(config.obj.x)) {
                            defaultX = config.obj.x;
                        }
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * et) * config.x;
                    }
                }
            },
            onComplete: () => {
                config.obj.x = defaultX;
                config.obj.y = defaultY;
            }
        });
        tl.to(this, {duration: config.duration});
        return tl;
    }

    /**
     * This is a new approach to this problem, with the focus being to really tie to the kinds of animations together to make a "gsap safe"
     * spine animation that actually follows the playHead of any parent timeline and supports other gsap features, such as reverse(), seek() etc.
     * Instead of just starting and stopping animations at different times.
     *
     * Caveat:
     * Does only set a new spine animation at beginning and end of the timeline.
     * So if you try to play the timeline from, say halfway into the animation, and this is not the already set spine animation.
     * It will play the current set spine animation. So playing animations not completely from the start or end (reverse(0)), will probably
     * cause you lots of headache.
     *
     * Easing do work. But be a bit cautious with easing that have overshoot. E.g. Back, or Elastic etc.
     * Because we are only animating the play head on the spine animation, there is no "overshoot". You cant play the animation longer than it is.
     *
     * !!! YOU NEED TO SET "autoUpdate = false" on your spine object.
     *
     *
     * @param spine PIXI.spineSpine object
     * @param trackIndex
     * @param animationName
     * @param loop
     * @param animationAlpha
     * @param mixDuration
     * @param resetOnComplete
     * @private
     */
    public static createTimelineForAnimation(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, loop: boolean = false, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            repeat: loop ? -1 : 0
        });

        if (!spine.spineData || !spine.state) {
            return tl;
        }

        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            console.error(`No animation named ${animationName}`);

            return tl;
        }

        spine.autoUpdate = false;

        const setAnimation: () => void = () => {
            if (spine.state) {
                const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
                trackEntry.alpha = animationAlpha;
                trackEntry.mixDuration = mixDuration;
            }
        };

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        };

        const updateFunc: () => void = () => {
            if (spine && target && spine.state) {
                const now = target.playHead;
                spine.update(now - target.prevTime);
                target.prevTime = now;
            }
        };

        tl.add(() => {
            target.prevTime = 0;
            setAnimation();
        });
        tl.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        });
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.to(target, {duration: 0.0001}); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc();
        });

        return tl;
    }

    public static expandGrid(extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // no expansion should happen, if it's already max expanded
        if (((this.ACTIVE_REELS_NUM === this.TOTAL_REELS_NUM) && (this.ACTIVE_ROWS_NUM === this.TOTAL_REELS_NUM)) && !Game.model.isRestore) {
            return tl;
        }

        const prevScaleFactor: number = this.scaleFactor;
        if ((this.ACTIVE_REELS_NUM + extraReels === (this.TOTAL_REELS_NUM)) || (this.ACTIVE_ROWS_NUM + extraRows === (this.TOTAL_REELS_NUM))) {
            this.scaleFactor = 0.7;
        } else if ((this.ACTIVE_REELS_NUM + extraReels === (this.TOTAL_REELS_NUM - 1)) || (this.ACTIVE_ROWS_NUM + extraRows === (this.TOTAL_REELS_NUM - 1))) {
            this.scaleFactor = 0.8;
        }

        if (Game.model.isRestore) {
            if ((this.ACTIVE_REELS_NUM === (this.TOTAL_REELS_NUM - 1)) || (this.ACTIVE_ROWS_NUM === (this.TOTAL_REELS_NUM - 1))) {
                this.scaleFactor = 0.8;
            } else if ((this.ACTIVE_REELS_NUM === (this.TOTAL_REELS_NUM)) || (this.ACTIVE_ROWS_NUM === (this.TOTAL_REELS_NUM))) {
                this.scaleFactor = 0.7;
            }
        }

        this.hasGridExpanded = true;

        if (this.scaleFactor !== prevScaleFactor) {
            this.SYMBOL_WIDTH = (this.scaleFactor * this.DEFAULT_SYMBOL_WIDTH);
            this.SYMBOL_HEIGHT = (this.scaleFactor * this.DEFAULT_SYMBOL_HEIGHT);

            tl.add(this.backgroundElements.onGridExpansion(this.scaleFactor, (this.scaleFactor === 0.8) ? 72 : 108, 0.2));
        }

        const xOffset: number = (this.scaleFactor === 0.8 ? 35 : 41),
            yOffset: number = (this.scaleFactor === 0.8 ? 35 : 40);

        tl.add([
            this.symbolFloors.doGridExpansion(extraReels, extraRows, addReelsToRight, addRowsInBottom, 0.2),
            this.reelsView.doGridExpansion(extraReels, extraRows, addReelsToRight, addRowsInBottom, xOffset, yOffset, 0.2),
            this.monsterReelsView.expandGrid(xOffset, yOffset, 15, ((Math.min((extraRows + this.ACTIVE_ROWS_NUM), this.TOTAL_REELS_NUM)) * Game.SYMBOL_HEIGHT) + 50, 0.2),
            // Game.overlaysForSymbols.handleGridExpansionEffectOnGE(0.2, xOffset, yOffset) // todo fix this and make sure #341 doesn't occur
        ], 0);

        if (!Game.model.isRestore) {
            this.ACTIVE_REELS_NUM = Math.min((extraReels + this.ACTIVE_REELS_NUM), this.TOTAL_REELS_NUM);
            this.ACTIVE_ROWS_NUM = Math.min((extraRows + this.ACTIVE_ROWS_NUM), this.TOTAL_REELS_NUM);
            Game.gridSize = this.ACTIVE_REELS_NUM + "by" + this.ACTIVE_ROWS_NUM;
        }

        let cr1ReelId: number = 0,
            cr1RowId: number = 0,
            cr2ReelId: number = 0,
            cr2RowId: number = 0;
        if (extraReels) {
            if (addReelsToRight) {
                cr1ReelId = (this.ACTIVE_REELS_NUM - 1);
                cr2ReelId = (this.ACTIVE_REELS_NUM - 1);
            }

            cr1RowId = 0;
            cr2RowId = (this.ACTIVE_ROWS_NUM - 1);
        } else {
            cr2ReelId = (this.ACTIVE_REELS_NUM - 1);

            if (addRowsInBottom) {
                cr1RowId = (this.ACTIVE_ROWS_NUM - 1);
                cr2RowId = (this.ACTIVE_ROWS_NUM - 1);
            }
        }

        const leftCR: Symbol = this.reelsView.reelModels[cr1ReelId].getSymbolAt(cr1RowId, "P4"),
            rightCR: Symbol = this.reelsView.reelModels[cr2ReelId].getSymbolAt(cr2RowId, "P4");

        leftCR.updateTexture("CR");
        leftCR.isRemoved = false;

        rightCR.updateTexture("CR");
        rightCR.isRemoved = false;

        if (Game.mxSymbol && extraReels) {
            if (!addReelsToRight) {
                Game.mxVSymbolReelId += extraReels;
            }
        }

        tl.add(Game.reelCorners.moveCornerOnGridExpansion(0.2, this.ACTIVE_REELS_NUM + "by" + this.ACTIVE_ROWS_NUM), 0);

        if (this.scaleFactor !== prevScaleFactor) {
            tl.add(() => {
                Game.collectAreaHighlightView.onGridExpansion(xOffset, yOffset);
            }, 0);
        }
        tl.add(() => {
            Game.overlaysForSymbols.onGridExpansion();
        }, 0);

        // don't move this call up, as it consumes Game.gridSize which is prepared after grid has been called to resize
        tl.add([
            this.multiplier.onGridExpansion(Game.gridSize, 0.2),
            Game.repositionGridOnResize(Game.gridSize, 0.2),
            this.scatterInventory.onGridExpansion(Game.gridSize, 0.2),
            Game.wall.onGridExpansion(0.2, Game.gridSize),
            Game.symbolFloors.rePositionEntireThisOnGridResize(0.2, Game.gridSize)
        ], 0);

        return tl;
    }

    public static prepMonstersToDieAfterAvalanche(): void {
        const monsterPresentAfterAvalanche: string[] = [];
        const monsterDieAfterAvalanche: string[] = [];

        for (let reels of Game.model.data.reelsAfterAvalanche) {
            for (let symbol of reels) {
                if (Game.MONSTER_SYMBOLS.includes(symbol)) {
                    monsterPresentAfterAvalanche.push(symbol);
                }
            }
        }

        for (let monster of Game.MONSTER_SYMBOLS) {
            if (!monsterPresentAfterAvalanche.includes(monster)) {
                monsterDieAfterAvalanche.push(monster);
            }
        }

        Game.monstersToDieAfterAvalanche = monsterDieAfterAvalanche;
    }

    public static showExplodePatternHighlighterOnSymbols(reelId: number, fromRowId: number, toRowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }
                tl.add(Game.symbolFloors.playExplodePatternHighlightAnimation(reelId, rowId), 0);
            }
        }

        return tl;
    }

    public static showExplodePatternSparkOnSymbols(reelId: number, fromRowId: number, toRowId: number, duration: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }
                tl.add(Game.symbolFloors.playExplodePatternSparkAnimation(reelId, rowId, duration), 0);
            }
        }

        return tl;
    }

    public static explodeSymbols(reelId: number, fromRowId: number, toRowId: number, forceExplode: boolean = false, isPlayMonsterSurprised: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }

                const reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId],
                    symbol: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);

                if ((reelsSymName.includes("MX")) || (reelsSymName.includes("CR"))) {
                    continue;
                }

                let isOutOfLife: boolean = false;
                // #181
                if (this.MONSTER_SYMBOLS.includes(symbol.name)) {
                    if (isPlayMonsterSurprised && !symbol.isRemoved && !((Game.model.data.carriedSymbolUserNode?.x === reelId) && (Game.model.data.carriedSymbolUserNode?.y === rowId))) {
                        if (!Game.surprisedMonsters.includes(symbol.name)) {
                            Game.surprisedMonsters.push(symbol.name);
                        }
                        Game.soundPlayer.playSound(symbol.name + "Surprised" + MathHelper.randomIntInRange(1, 3), Game.getSurprisedSoundPlayVolume());
                    }
                    if (!symbol.isRemoved && Game.reelCorners.getLife(Game.MONSTER_NAME_TO_IDX[symbol.name]) === 1) {
                        isOutOfLife = true;
                        tl.add(this.removeSymbolFromAReel(reelId, rowId, forceExplode, false, false), 0);////#377 false to avoid explode
                    }
                }

                if (Game.isPlayXLootExplode && this.PAYOUT_SYMBOLS.includes(symbol.name)) {
                    tl.add(() => {
                        Game.soundPlayer.playSound(Sound.LOOT_VANISH, 1);
                    }, 0);
                    Game.isPlayXLootExplode = false;
                }

                if (!isOutOfLife) {
                    tl.add(this.removeSymbolFromAReel(reelId, rowId, forceExplode), 0);
                }
            }
        }

        return tl;
    }

    public static explodeSymbolsBeforeMaxExpansion(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            maxTriggerSymbols: Symbol[] = [];

        Game.maxExpFeatureSymPos.forEach((pos: string) => {
            const splitPos: string[] = pos.split("_"),
                reelId: number = +splitPos[0],
                rowId: number = +splitPos[1],
                reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId];

            if (Game.reelsView.reelModels[reelId]) {
                const featureSymbol: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);
                if (featureSymbol) {
                    if (featureSymbol.name === "XB") {
                        tl.add(featureSymbol.playXBombExplodeAnimation(XBombStates.TRIGGER), 0)
                            .add(featureSymbol.playXBombExplodeAnimation(XBombStates.EXPLODE));
                    } else if (featureSymbol.name === "GE") {
                        tl.add(featureSymbol.getStateAnimation(GEStates.TRIGGER_MAX), 0)
                            .add(Game.overlaysForSymbols.playEffectAnimationGE(featureSymbol, GEStates.TRIGGER_MAX));
                    }

                    featureSymbol.isRemoved = true;
                    if (featureSymbol.parent) {
                        featureSymbol.parent.addChild(featureSymbol);

                        // #150
                        maxTriggerSymbols.push(featureSymbol);
                    }
                }
            }
        });

        tl.addLabel("doExplosion", 0.5);

        // #126
        Game.maxExpFeatureSymPos.forEach((pos: string) => {
            const splitPos: string[] = pos.split("_"),
                reelId: number = +splitPos[0],
                rowId: number = +splitPos[1],
                reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId];

            if (Game.reelsView.reelModels[reelId]) {
                const featureSymbol: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);
                if (featureSymbol && featureSymbol.name === "XB") {
                    tl.add(Game.multiplier.updateMultiplier(1, true), "doExplosion");
                }
            }
        });

        for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
            Game.updateSurprisedMonstersInExplosion(reelId, 0, Game.ACTIVE_ROWS_NUM);
            tl.add(Game.explodeSymbols(reelId, 0, Game.ACTIVE_ROWS_NUM, true), "doExplosion");
            tl.add(() => {
                Game.soundPlayer.playSound("explosion" + MathHelper.randomIntInRange(1, 4), 1);
            }, "doExplosion");
        }

        // #150
        tl.add(() => {
            maxTriggerSymbols.forEach((sym: Symbol) => {
                if (sym && sym.parent) {
                    sym.remove();
                }
            });

            Game.maxExpFeatureSymPos = [];
        }, "doExplosion");

        return tl;
    }

    public static getSurprisedSoundPlayVolume(): number {
        // 1 Surprised = 100% Volume
        // 2 Surprised = 90% Volume
        // 3 Surprised = 85% Volume
        // 4 Surprised = 80% Volume 
        const surprisedMonstersLen: number = Game.surprisedMonsters.length;
        let playVol: number = 0;

        if (surprisedMonstersLen === 4) {
            playVol = 0.8;
        } else if (surprisedMonstersLen === 3) {
            playVol = 0.85;
        } else if (surprisedMonstersLen === 2) {
            playVol = 0.9;
        } else if (surprisedMonstersLen === 1) {
            playVol = 1;
        }

        return playVol;
    }

    public static updateSurprisedMonstersInExplosion(reelId: number, fromRowId: number, toRowId: number): void {
        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId],
                    symbol: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);

                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }

                if (symbol && this.MONSTER_SYMBOLS.includes(symbol.name)) {
                    if (!symbol.isRemoved && !((Game.model.data.carriedSymbolUserNode?.x === reelId) && (Game.model.data.carriedSymbolUserNode?.y === rowId)) && !Game.surprisedMonsters.includes(symbol.name)) {
                        Game.surprisedMonsters.push(symbol.name);
                    }
                }
            }
        }
    }

    public static updateSurprisedMonsters(monstersToExplode: string[], attackingMonster: MonsterSymbol): void {
        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
            if (monstersToExplode.includes(monster.name)) {
                if (monster.name !== attackingMonster.name && !Game.surprisedMonsters.includes(monster.name)) {
                    Game.surprisedMonsters.push(monster.name);
                }
            }
        });
    }

    public static canRemoveSymbol(sym: Symbol): boolean {
        return (sym && !sym.isRemoved && (Game.ORDINARY_PAYOUT_SYMBOLS.includes(sym.name) || Game.MONSTER_SYMBOLS.includes(sym.name)));
    }

    public static isBoostedBetItems(): boolean {
        return (Game.model.data.mode === GameModeNames.NORMAL && Game.model.buyFeatureName === "BOOSTED_BET_ITEMS");
    }

    public static hasMXLandedAtOnce(): boolean {
        return Game.isMaxWinByMXSymbol() && Game.model.gameData.mxLeftBottomCorner.x === Game.model.gameData.mxLeftBottomCornerAtStart.x && Game.model.gameData.mxLeftBottomCorner.y === Game.model.gameData.mxLeftBottomCornerAtStart.y;
    }

    public static removeSymbolFromAReel(reelId: number, rowId: number, forceExplode: boolean = false, shouldExplode: boolean = true, isKillCount: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // #117 -> don't access out of bound symbol
        if (reelId < 0 || reelId >= Game.ACTIVE_REELS_NUM || rowId < 0 || rowId >= Game.ACTIVE_ROWS_NUM) {
            return tl;
        }

        let reelsSymName: string = Game.model.gameData.evaluatedArea[reelId][rowId];

        if (isKillCount) {
            reelsSymName = Game.model.gameData.reelsAfterAvalanche[reelId][rowId];
        }
        const sym: Symbol = this.reelsView.reelModels[reelId].getSymbolAt(rowId, reelsSymName);

        if (!sym || reelsSymName === "MX" || sym.name === "CR" || sym.name.includes("MX")) {
            return tl;
        }
        const symName: string = sym.name;
        let isOOL: boolean = false,
            isPlayDeadSkull = false;

        if (Game.model.gameData.maxExpansion && forceExplode && ['GE', 'XB'].includes(symName)) {
            const pos: string = (reelId + "_" + rowId);
            if (!Game.maxExpFeatureSymPos.includes(pos)) {
                Game.maxExpFeatureSymPos.push(pos);
            }
        }

        if (!sym.isRemoved && (this.ORDINARY_PAYOUT_SYMBOLS.includes(symName) || this.MONSTER_SYMBOLS.includes(symName))) {
            sym.isRemoved = true;

            if (this.MONSTER_SYMBOLS.includes(symName)) {
                if (Game.reelCorners.getLife(Game.MONSTER_NAME_TO_IDX[sym.name]) === 1) { //#377
                    isOOL = true;
                    if (Game.monsterSymbolsGoingDead && !Game.monsterSymbolsGoingDead.includes(sym)) {
                        Game.monsterSymbolsGoingDead.push(sym);

                        if (Game.isPlayMonsterDied) {
                            tl.add(() => {
                                Game.soundPlayer.playSound(Sound.FUNERAL + MathHelper.randomIntInRange(1, 3), 1)
                            }, 0);

                            Game.isPlayMonsterDied = false;
                            isPlayDeadSkull = true;
                        }
                    }

                    tl.add([
                        sym.getStateAnimation(MONSTER_WEAPON_ACTIONS.OUT_OF_LIVES, 5),
                        sym.getStateAnimation(SymbolStates.NORMAL, 6)
                    ]);
                }
                tl.addLabel("startExplode");
                tl.add(Game.reelCorners.reduceLifeOfCorner(this.MONSTER_NAME_TO_IDX[symName], isPlayDeadSkull), "startExplode");
                tl.add(Game.symbolFloors.showBloodStain(reelId, rowId), "startExplode");
            } else {
                tl.addLabel("startExplode");
            }
            if (shouldExplode && !isOOL) {
                tl.add(sym.playExplodeEffect(isKillCount), "startExplode");
            } else if (!isOOL) {
                tl.add(sym.remove());
            } else if (isOOL) {
                tl.add(() => {
                    Game.soundPlayer.playSound(Sound.LOOT_VANISH, 1);
                }, 0)
            }
        }

        return tl;
    }

    public static getSymbolPosFromReel(symbolName: string): IFeatureData[] {
        const posArr: IFeatureData[] = [];

        this.reelsView.reelModels.forEach((reel: ReelModel, reelId: number) => {
            reel.symbols.forEach((symbol: Symbol, rowId: number) => {
                if (symbol.parent && symbol.name === symbolName) {
                    posArr.push({
                        reelId,
                        rowId
                    });
                }
            });
        });

        return posArr;
    }

    public static handleCornerRemoval(corner1ReelId: number, corner1RowId: number, corner2ReelId: number, corner2RowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            corner1: Symbol = Game.reelsView.reelModels[corner1ReelId].getSymbolAt(corner1RowId, "CR"),
            corner2: Symbol = Game.reelsView.reelModels[corner2ReelId].getSymbolAt(corner2RowId, "CR");

        if (Game.gridSize === "9by9") {
            return tl;
        }

        if (((corner1ReelId === 0) || (corner1ReelId === Game.ACTIVE_REELS_NUM - 1)) && (!corner1.name.includes("MX"))) {
            tl.add(Game.reelsView.reelModels[corner1ReelId].removeSymbol(corner1));
            tl.add(() => {
                corner1.updateTexture("P4");
                corner1.isRemoved = true;
            });
        }
        if (((corner2ReelId === 0) || (corner2ReelId === Game.ACTIVE_REELS_NUM - 1)) && (!corner2.name.includes("MX"))) {
            tl.add(Game.reelsView.reelModels[corner2ReelId].removeSymbol(corner2));
            tl.add(() => {
                corner2.updateTexture("P4");
                corner2.isRemoved = true;
            });
        }

        return tl;
    }

    public static cornerRemovalUtils(extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean): IHandleCorner {
        let leftCRReelId: number = 0, leftCRRowId: number = 0, rightCRReelId: number = Game.ACTIVE_REELS_NUM - 1,
            rightCRRowId: number = Game.ACTIVE_ROWS_NUM - 1;

        if (extraReels) {
            if (addReelsToRight) {
                leftCRReelId = Game.ACTIVE_REELS_NUM - 1;
                leftCRRowId = 0;
                rightCRReelId = Game.ACTIVE_REELS_NUM - 1;
                rightCRRowId = Game.ACTIVE_ROWS_NUM - 1;
            } else {
                leftCRReelId = 0;
                leftCRRowId = 0;
                rightCRReelId = 0;
                rightCRRowId = Game.ACTIVE_ROWS_NUM - 1;
            }
        }

        if (extraRows) {
            if (addRowsInBottom) {
                leftCRReelId = 0;
                leftCRRowId = Game.ACTIVE_ROWS_NUM - 1;
                rightCRReelId = Game.ACTIVE_REELS_NUM - 1;
                rightCRRowId = Game.ACTIVE_ROWS_NUM - 1;
            } else {
                leftCRReelId = 0;
                leftCRRowId = 0;
                rightCRReelId = Game.ACTIVE_REELS_NUM - 1;
                rightCRRowId = 0;
            }
        }

        return {leftCRReelId, leftCRRowId, rightCRReelId, rightCRRowId};
    }

    public static isSingleWinBelowStake(): boolean {
        return Game.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (Game.model.gameData.spinWin <= this.getBuyFeaturePrice(true));
    }

    public static isWinBelowStake(): boolean {
        return Game.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (Game.model.data.accumulatedRoundWin <= this.getBuyFeaturePrice(false));
    }

    public static repositionGridOnResize(gridName: string, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            [xOffset, yOffset] = this.getGridContainerOffset(gridName);

        tl.to([this.reelsView, this.monsterReelsView, this.reelCorners, this.collectAreaHighlightView], {
            duration: duration,
            x: xOffset,
            y: yOffset
        }, 0);

        return tl;
    }

    // #28
    public static handleSpecialsInRespectivePathHighlight(path: IMonsterWalk[], monsterName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            idx: number = +(monsterName[1]) - 1;

        for (let i: number = 1; i < path.length; i++) {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[path[i].x][path[i].y],
                symbol: Symbol = Game.reelsView.reelModels[path[i].x].getSymbolAt(path[i].y, reelsSymName);

            if (symbol) {
                if (symbol.name === "W") {
                    symbol.isDark = false; // #367

                    tl.add([
                        () => {
                            const multiplierSkinLevel: string = Game.multiplier.getSkinLevel();

                            symbol.changeSymbolSkin(WILD_SKIN_NAMES[monsterName] + multiplierSkinLevel);
                        },
                        symbol.getStateAnimation((symbol.isSplit ? WildStates.SPLIT_BASE : SymbolStates.NORMAL)),
                        symbol.updateHighlight(idx)
                    ], 0);
                } else if (Game.BOOSTER_SYMBOLS.includes(symbol.name) || (symbol.name === "S") || (!symbol.name.includes("MX") && symbol.name.charAt(0) === "M")) {
                    tl.add([
                        symbol.updateHighlight(idx)
                    ], 0);
                }
            }
        }

        return tl;
    }

    public static clearPlayingTrack(animation: PIXI.spine.Spine, trackIndex: number | undefined = undefined, mixDuration: number = 0): void {
        if (trackIndex === undefined) {
            animation.state.clearTracks();
        } else {
            animation.state.clearTrack(trackIndex);
            animation.state.setEmptyAnimation(trackIndex, mixDuration);
        }
        animation.skeleton.setToSetupPose();
        animation.skeleton.setSlotsToSetupPose();

        animation.autoUpdate = false;
    }

    public static isMonsterMove(): boolean {
        return Game.model.gameData.monsters.some((monster: IMonster) => monster.path && monster.path.length > 1);
    }

    public static isMaxWinByMXSymbol(): boolean {
        return Game.model.data.brokeTheBank && Game.model.gameData.mxLeftBottomCornerAtStart && Object.keys(Game.model.gameData.mxLeftBottomCornerAtStart).length > 0;
    }

    public static isSingleMover(monster: IMonster, path: IMonsterWalk[]): boolean {
        for (let i: number = 0; i < path.length; i++) {
            if (monster.isSingleMover && (monster.lastMultiplierPosition.x === path[i].x && monster.lastMultiplierPosition.y === path[i].y)) {
                return true;
            }
        }

        return false;
    }

    public static isSymOnDiffRow(data: string[][], symbol: string, reelId: number, rowId: number): boolean {
        for (let j: number = 0; j < reelId; j++) {
            for (let i: number = 0; i < Game.ACTIVE_ROWS_NUM; i++) {
                if (i === rowId && data[j][i] === symbol) {
                    return false;
                }
            }
        }

        return true;
    }

    public static wasMayhemBonusBuy(): boolean {
        return Game.model.data.wasFeatureBuy && (Game.model.buyFeatureName === "FREESPIN_MAYHEM" || Game.model.data.lifeLeftInitial === 1);
    }

    public static getBuyFeaturePrice(fromSingleWin: boolean): number {
        let betLevel: string = Game.apiPlugin.freeBets.hasFreeBets() ? Game.apiPlugin.freeBets.getBet() : Game.apiPlugin.betLevel.getLevel();
        let price: number = Game.model.gameData?.playedBetValue || parseFloat(betLevel);

        if ((!fromSingleWin) || (fromSingleWin && Game.model.gameData?.boostedBet)) {
            for (let index: number = 0; index < Game.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Game.model.initData.featureBuyTimesBetValue[index].name === Game.model.buyFeatureName) {
                    price *= Game.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    public static createTimelineSprite(assetStr: string, sourceFPS: number = 30, pos?: PIXI.Point | undefined, isCenter?: boolean): TimelineSprite {
        const tls: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures(assetStr), sourceFPS);
        if (pos) {
            tls.position.set(pos.x, pos.y);
        }
        if (isCenter) {
            tls.anchor.set(0.5);
        }
        return tls;
    }

    public static getMultiplierLvl(multiplierName: string): string {
        let multiplierVal: number = +multiplierName.substring(1);
        let multiplierLvl: string = '';

        if (multiplierVal >= 2 && multiplierVal <= 6) {
            multiplierLvl = "lvl1";
        } else if (multiplierVal > 6 && multiplierVal <= 50) {
            multiplierLvl = "lvl2";
        } else if (multiplierVal > 50 && multiplierVal <= 500) {
            multiplierLvl = "lvl3";
        } else if (multiplierVal > 500) {
            multiplierLvl = "lvl4";
        }

        return multiplierLvl;
    }

    // FS/SFS triggered but not Mayhem spins
    public static isFreespinTriggered(): boolean {
        return (Game.model.data.mode.indexOf("NORMAL") > -1 && Game.model.gameData.nextMode === GameModeNames.FREESPIN && !Game.model.data.mayhemSpin);
    }

    // in FS/SFS but not Mayhem spins
    public static isFreespin(): boolean {
        return (Game.model.data.mode.indexOf(GameModeNames.FREESPIN) > -1 && !Game.model.data.mayhemSpin);
    }

    public static getCurrentGameMode(): string {
        return Game.isFreespin() ? (Game.isSFSActive ? GameModeNames.SUPER_FREESPIN : GameModeNames.FREESPIN) : (Game.model.data.mayhemSpin ? GameModeNames.MAYHEM : GameModeNames.NORMAL);
    }

    public static getMonstersToExplode(reelId: number, fromRowId: number, toRowId: number, monstersToExplode: string[]): void {
        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }

                const reelsSymName: string = this.model.gameData.reels[reelId][rowId];

                if (this.MONSTER_SYMBOLS.includes(reelsSymName) && !monstersToExplode.includes(reelsSymName)) {
                    monstersToExplode.push(reelsSymName);
                }
            }
        }
    }

    public static checkIfTriggeringMonsterIsPartOfExplosion(reelId: number, fromRowId: number, toRowId: number, monsterSymbolToCheck: string): boolean {
        if (reelId > -1 && reelId < this.ACTIVE_REELS_NUM) {
            for (let rowId: number = fromRowId; rowId < toRowId; rowId++) {
                if (rowId < 0 || rowId >= this.ACTIVE_ROWS_NUM) {
                    continue;
                }

                const reelsSymName: string = this.model.gameData.reels[reelId][rowId];
                if (reelsSymName === monsterSymbolToCheck) {
                    return true;
                }
            }
        }

        return false;
    }

    public static shakeScreen(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            shakeObj: IShakeConfig = {
                obj: Game?.reelsView?.parent,
                x: 1,
                y: 1,
                hertz: 150,
                intensity: 10,
                duration: 0.2
            };

        if (this.model.isRestore) {
            return tl;
        }

        tl.add(Game.shake(shakeObj), 0);

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.EARTHQUAKE, 1);
        }, 0);

        tl.add(() => {
            Game.symbolFloors.stopExplodePatternHighlightAnimation();
        }, 0.5);

        return tl;
    }

    public static resetExpandedElements(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        const xOffset: number = 0,
            yOffset: number = 0,
            resetGridSize: string = "6by6",
            duration: number = 0.1;

        Game.scaleFactor = 1;
        Game.SYMBOL_WIDTH = Game.DEFAULT_SYMBOL_WIDTH;
        Game.SYMBOL_HEIGHT = Game.DEFAULT_SYMBOL_HEIGHT;

        tl.add([
            Game.backgroundElements.onGridExpansion(1, 0, duration),
            Game.monsterReelsView.expandGrid(xOffset, yOffset, 25, 670, duration),
            Game.symbolFloors.resetGrid(duration, resetGridSize),
            Game.reelCorners.moveCornerOnGridExpansion(duration, resetGridSize),
            Game.repositionGridOnResize(resetGridSize, duration),
            Game.reelsView.resetGrid(duration),
            Game.multiplier.onGridExpansion(resetGridSize, duration),
            Game.scatterInventory.onGridExpansion(resetGridSize, duration),
            Game.wall.onGridExpansion(duration, resetGridSize),
            Game.symbolFloors.rePositionEntireThisOnGridResize(duration, resetGridSize)
        ]);

        Game.ACTIVE_REELS_NUM = Game.DEFAULT_REELS_NUM;
        Game.ACTIVE_ROWS_NUM = Game.DEFAULT_ROWS_NUM;

        Game.gridSize = resetGridSize;

        while (Game.reelsView.reelModels.length > Game.DEFAULT_REELS_NUM) {
            Game.reelsView.reelModels.pop();
        }

        Game.overlaysForSymbols.onGridExpansion();
        Game.collectAreaHighlightView.onGridExpansion(xOffset, yOffset);

        return tl;
    }

    public static getReelGridSize(reelsData: string[][]): void {
        let reelLen: number = 0, rowLen: number = 0;

        // #259
        if ((Game.model.data.mode.includes("NORMAL") && Game.model.data.nextMode.includes("FREESPIN")) && (Game.model.data.mayhemSpin || Game.model.data.scatterCount > 3)) {
            reelLen = rowLen = 9;
        } else {
            // get rows
            for (let sym of reelsData[0]) {
                if (sym !== 'L') {
                    rowLen++;
                } else {
                    break;
                }
            }

            // get reels
            for (let reel of reelsData) {
                if (reel[0] !== 'L') {
                    reelLen++;
                } else {
                    break;
                }
            }
        }

        Game.ACTIVE_REELS_NUM = reelLen;
        Game.ACTIVE_ROWS_NUM = rowLen;

        Game.gridSize = reelLen + "by" + rowLen;
    }

    public static expandGridForMaxExpansion(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const expansionDirections: number[][] = Game.model.gameData.expansionDirections;

        let extraReels: number = 0, extraRows: number = 0;
        for (let i: number = 0; i < expansionDirections.length; i++) {
            const [reelDir, rowDir] = expansionDirections[i],
                prevReelsNum: number = Game.ACTIVE_REELS_NUM,
                prevRowsNum: number = Game.ACTIVE_ROWS_NUM;
            let newReelsCount: number = (reelDir === 0 ? 0 : (Game.ACTIVE_REELS_NUM === 8 ? 1 : 2));
            let newRowsCount: number = (rowDir === 0 ? 0 : (Game.ACTIVE_ROWS_NUM === 8 ? 1 : 2));
            let isCurrReelsToRight: boolean = false, isCurrRowsInBottom: boolean = false;

            if (extraReels === 3 && extraRows === 3) {
                break;
            }

            if (reelDir !== 0) {
                extraReels += newReelsCount;
                isCurrReelsToRight = extraReels > 0 && reelDir === 1;
            }

            if (rowDir !== 0) {
                extraRows += newRowsCount;
                isCurrRowsInBottom = extraRows > 0 && rowDir === 1;
            }

            const {
                leftCRReelId,
                leftCRRowId,
                rightCRReelId,
                rightCRRowId
            } = Game.cornerRemovalUtils(newReelsCount, newRowsCount, isCurrReelsToRight, isCurrRowsInBottom);
            tl.add([
                Game.handleCornerRemoval(leftCRReelId, leftCRRowId, rightCRReelId, rightCRRowId),
                Game.expandGrid(newReelsCount, newRowsCount, isCurrReelsToRight, isCurrRowsInBottom)
            ], 0);

            tl.add(Game.showMaxExplodePatternAfterExpansion(isCurrReelsToRight, isCurrRowsInBottom, prevReelsNum, prevRowsNum), 0);
        }

        return tl;
    }

    public getNoWinGameData(): any {
        return {
            "reels": [
                [
                    "CR",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P1",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P2",
                    "P2",
                    "C3",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P2",
                    "P2",
                    "C1",
                    "P4",
                    "C2",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P3",
                    "P3",
                    "P3",
                    "P3",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "CR",
                    "C4",
                    "P2",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ]
            ],
            "win": 0,
            "accumulatedRoundWin": 0,
            "playedBetValue": 1,
            "mode": "NORMAL",
            "nextMode": "NORMAL",
            "wasFeatureBuy": false,
            "boostedBet": false,
            "brokeTheBank": false,
            "freespinsTriggeredThisSpin": false,
            "freespinsPlayed": 0,
            "freespinsLeft": 0,
            "freespinsWinnings": 0,
            "spinWin": 0,
            "symbolSizes": [
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ]
            ],
            "multiplier": 1,
            "reelsBeforeAvalanche": [
                [
                    "CR",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P1",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P2",
                    "P2",
                    "C3",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P2",
                    "P2",
                    "C1",
                    "P4",
                    "C2",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P3",
                    "P3",
                    "P3",
                    "P3",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "CR",
                    "C4",
                    "P2",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ]
            ],
            "reelsAfterAvalanche": [
                [
                    "CR",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P1",
                    "P1",
                    "P4",
                    "P4",
                    "P4",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P2",
                    "P2",
                    "C3",
                    "P1",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P2",
                    "P2",
                    "C1",
                    "P4",
                    "C2",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "P2",
                    "P1",
                    "P3",
                    "P3",
                    "P3",
                    "P3",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "CR",
                    "C4",
                    "P2",
                    "P4",
                    "P4",
                    "CR",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ],
                [
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L",
                    "L"
                ]
            ],
            "monsters": [
                {
                    "symbol": 4,
                    "path": [
                        {
                            "x": 3,
                            "y": 3
                        }
                    ],
                    "graph": {},
                    "reachablePositions": [
                        {
                            "x": 3,
                            "y": 3
                        }
                    ],
                    "reachableMultipliers": [],
                    "reachableValuables": [],
                    "reachableMultiplierSum": 0,
                    "collectWins": [],
                    "lifeLeft": 3,
                    "carriedSymbols": [],
                    "postAvalanchePosition": {
                        "x": 3,
                        "y": 3
                    },
                    "isSingleMover": false,
                    "lastMultiplierPosition": {
                        "x": 3,
                        "y": 3
                    },
                    "collectedScatterCount": 0
                },
                {
                    "symbol": 5,
                    "path": [
                        {
                            "x": 3,
                            "y": 5
                        }
                    ],
                    "graph": {},
                    "reachablePositions": [
                        {
                            "x": 3,
                            "y": 5
                        }
                    ],
                    "reachableMultipliers": [],
                    "reachableValuables": [],
                    "reachableMultiplierSum": 0,
                    "collectWins": [],
                    "lifeLeft": 3,
                    "carriedSymbols": [],
                    "postAvalanchePosition": {
                        "x": 3,
                        "y": 5
                    },
                    "isSingleMover": false,
                    "lastMultiplierPosition": {
                        "x": 3,
                        "y": 5
                    },
                    "collectedScatterCount": 0
                },
                {
                    "symbol": 6,
                    "path": [
                        {
                            "x": 2,
                            "y": 4
                        }
                    ],
                    "graph": {},
                    "reachablePositions": [
                        {
                            "x": 2,
                            "y": 4
                        }
                    ],
                    "reachableMultipliers": [],
                    "reachableValuables": [],
                    "reachableMultiplierSum": 0,
                    "collectWins": [],
                    "lifeLeft": 3,
                    "carriedSymbols": [],
                    "postAvalanchePosition": {
                        "x": 2,
                        "y": 4
                    },
                    "isSingleMover": false,
                    "lastMultiplierPosition": {
                        "x": 2,
                        "y": 4
                    },
                    "collectedScatterCount": 0
                },
                {
                    "symbol": 7,
                    "path": [
                        {
                            "x": 5,
                            "y": 1
                        }
                    ],
                    "graph": {},
                    "reachablePositions": [
                        {
                            "x": 5,
                            "y": 1
                        }
                    ],
                    "reachableMultipliers": [],
                    "reachableValuables": [],
                    "reachableMultiplierSum": 0,
                    "collectWins": [],
                    "lifeLeft": 3,
                    "carriedSymbols": [],
                    "postAvalanchePosition": {
                        "x": 5,
                        "y": 1
                    },
                    "isSingleMover": false,
                    "lastMultiplierPosition": {
                        "x": 5,
                        "y": 1
                    },
                    "collectedScatterCount": 0
                }
            ],
            "airStrikeRocketCoordinates": [],
            "expansionDirections": [],
            "gridExpanderDirections": [],
            "scatterCount": 0,
            "mayhemSpin": false,
            "maxExpansion": false
        };
    }

    public init(): Promise<NolimitPlugin> {
        return super.init().then((value: NolimitPlugin) => {
            Game.model = new Model(SlotGame.apiPlugin);
            Game.gameFlowController = new GameFlowController(SlotGame.apiPlugin, Game.model);
            Game.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            // Example
            Game.model.events?.on(GameModelEvent.GAME, (data: IGameData) => {
                console.log(data);
            });

            const winPres: WinPresentation = new WinPresentation(),
                reelStart: NormalBetPresentation = new NormalBetPresentation(),
                normalResult: NormalResultPresentation = new NormalResultPresentation(),
                normalBetIdle: NormalBetIdlePresentation = new NormalBetIdlePresentation(),
                zeroBetIdle: ZeroBetIdlePresentation = new ZeroBetIdlePresentation(),
                avalancheFillUp: AvalancheFillUpPresentation = new AvalancheFillUpPresentation();

            Game.gameFlowController.addGameModeFlowStatePresentationMap(GameModeNames.NORMAL, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, normalBetIdle],
                [FlowState.BET, reelStart],
                [FlowState.RESULT, normalResult],
                [FlowState.WIN, winPres]
            ]));
            Game.gameFlowController.addGameModeFlowStatePresentationMap(GameModeNames.NORMAL_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, zeroBetIdle],
                [FlowState.RESULT, avalancheFillUp],
                [FlowState.WIN, winPres]
            ]));

            Game.gameFlowController.addGameModeFlowStatePresentationMap(GameModeNames.FREESPIN, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, zeroBetIdle],
                [FlowState.BET, reelStart],
                [FlowState.RESULT, normalResult],
                [FlowState.WIN, winPres]
            ]));
            Game.gameFlowController.addGameModeFlowStatePresentationMap(GameModeNames.FREESPIN_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, zeroBetIdle],
                [FlowState.RESULT, avalancheFillUp],
                [FlowState.WIN, winPres]
            ]));

            Game.normalIdle = normalBetIdle;
            this.handleSweepInit();
            return value;
        });
    }

    public getReady(): Promise<NolimitPlugin> {
        // Show intro and load game assets
        return super.getReady().then((value: NolimitPlugin) => {
            const fontLoader: FontLoader = new FontLoader(NolimitApplication.resourcePath);
            fontLoader.add(OpenSans.ITALIC_600);
            fontLoader.add(OpenSans.NORMAL_300);
            fontLoader.add(OpenSans.NORMAL_400);
            fontLoader.add(OpenSans.NORMAL_700);

            AssetsConfig.addToAssetsConfig(GameAssets.RESOURCES_GROUPS);
            Game.assetLoader = new AssetLoader(Game.apiPlugin, NolimitApplication.resourcePath + "/resources/");
            Game.hasGridExpanded = false;

            const promises: Promise<[boolean, any, any]> = Promise.all([fontLoader.load(), Game.assetLoader.load(ResourcesGroupName.MAIN), this.handleSweepGetReady()]);

            return promises.then(() => {
                if (Game.model.isRestore) {
                    // #151
                    if (Game.model.data.mode.indexOf("FREESPIN") !== -1) {
                        Game.keypadPlugin.setZeroBetSpinCounter(Math.max(Game.model.data.freespinsLeft, 0));
                    }

                    Game.resizeReelsOnStart();
                }

                this.createViewsAndComponents();

                Game.repositionGridOnResize(Game.gridSize, 0);

                if (Game.scaleFactor !== 1) {
                    Game.multiplier.onGridExpansion(Game.gridSize, 0);
                    Game.scatterInventory.onGridExpansion(Game.gridSize, 0);

                    Game.symbolFloors.scaleExpandedGrid(0, (Game.gridSize[0] !== "6" ? 3 : 0));
                }

                return value;
            });
        });
    }

    public getReadyToStart(): Promise<NolimitPlugin> {
        // Game assets loaded, waiting for user to start game.
        return super.getReadyToStart().then((value: NolimitPlugin) => {
            return value;
        });
    }

    public start(): Promise<NolimitPlugin> {
        // User has pressed continue and the game starts.
        return super.start().then((value: NolimitPlugin) => {
            Game.gameFlowController.start();

            if (Game.model.data.mayhemSpin) {
                Game.soundPlayer.fadeInAndStartSound({
                    name: Sound.MAYHEM_LOOP,
                    from: 0,
                    to: 1,
                    duration: 2000,
                    repeat: Number.MAX_VALUE
                });
                Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
            } else {
                if (Game.model.data.mode.includes(GameModeNames.FREESPIN)) {
                    Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
                    setTimeout(() => {
                        Game.soundPlayer.fadeInAndStartSound({
                            name: Sound.FS_LOOP,
                            from: 0,
                            to: 1,
                            duration: 238,
                            repeat: Number.MAX_VALUE
                        });
                    }, 23360);
                } else {
                    Game.soundPlayer.playSound(Sound.AS_SNARE, 0, Number.MAX_VALUE);
                    Game.soundPlayer.playSound(Sound.SNARE_PLAYER, 0, Number.MAX_VALUE);
                    Game.soundPlayer.playSound(Sound.MAIN_BG_AMBIENCE, 1, Number.MAX_VALUE);
                    Game.soundPlayer.fadeInAndStartSound({
                        name: Sound.MAIN_GAME_MUSIC,
                        from: 0,
                        to: 1,
                        duration: 2000,
                        repeat: Number.MAX_VALUE
                    });
                }
            }

            if (Game.model.isRestore) {
                const totalWin: number = Game.model.initData.accumulatedRoundWin;
                Game.keypadPlugin.setWin(totalWin, true, Game.isWinBelowStake());

                Game.multiplier.resetMultiplier(Game.model.data.multiplier ?? 1);
                //#192
                this.animateSymbolsDuringRestore(Game.model.initData.symbolSizesAfterAvalanche);
                Game.noOfScatterLanded = Game.model.data.scatterCount;

                Game.backgroundElements.setMode(Game.model.data.nextMode);
            }

            return value;
        });
    }

    public getNolimitBonusDisplayConfiguration(): BonusFeatureDisplayConfigurationList {
        return {
            FREESPIN_3_SCATTERS: {
                displayName: "FREESPIN_4_SCATTERS",
                displayOrder: 0
            },
            FREESPIN_4_SCATTERS: {
                displayName: "FREESPIN_4_SCATTERS",
                displayOrder: 1
            },
            FREESPIN_MAYHEM: {
                displayName: "FREESPIN_MAYHEM",
                displayOrder: 2
            },
            GOD_MODE: {
                displayName: "GOD_MODE",
                displayOrder: 3,
                replacePopUpContent: (content: BonusBuyPopUpContent) => {
                    const warningText: Label = new Label(Game.apiPlugin.translations.translate("Warning: 1 spin!"), content.textLabel.getStyleClone());

                    warningText.scale.set(0.7);
                    warningText.anchor.set(0.5);
                    warningText.position.set(0, 400);
                    content.addChild(warningText);

                    content.textLabel.scale.set(0.8);
                    content.textLabel.position.y -= 30;

                    return content;
                }
            },
            LUCKY_DRAW: {
                displayName: "LUCKY_DRAW",
                displayOrder: 4
            },
            BOOSTED_BET: {
                displayName: "BOOSTED_BET",
                displayOrder: 5
            },
            BOOSTED_BET_ITEMS: {
                displayName: "BOOSTED_BET_ITEMS",
                displayOrder: 6
            }
        }
    }


    private paytable:string = require("../../nolimit/screens/paytable.mustache");
    public getPaytable(): Promise<string> {
        return new Promise((resolve, reject) => {
            const data = {
                skinPath: "screens/images/"
            };
            if (this.skinner.skinId != undefined) {
                data.skinPath = `../resources/images/${this.skinner.skinId}/`;
            }
            let symbolDataValueData: ISymbolValues = this._defaultSymbolValues;
            if (Game.model.initData.symbolValues) {
                symbolDataValueData = Game.model.initData.symbolValues;
                Logger.logDev("Using symbolValues from INIT data");
            } else {
                Logger.logDev("Using default symbolValues");
            }

            let paytableString: string = (<any>this.paytable).text;
            paytableString = paytableString.replace(/{{skinPath}}/g, data.skinPath);
            for (let key in symbolDataValueData) {
                paytableString = paytableString.replace(`{{${key}}}`, this.getSymbolString(key, symbolDataValueData[key]))
            }
            resolve(paytableString);
        });
    }


    private getSymbolString(key: string, data: { [numSymbols: string]: number }): string {
        return `
            <div class="symbol">
            <img src="{{gamePath}}screens/images/symbols/${key}.png">
            <div class="payout-group">
                <div class="payout">
                    <span class="number">1</span>
                    <span class="multiplier" data-multiplier="${data[1] * 0.05}">${data[1] * 0.05}</span>
                </div>
            </div>
            </div>
        `;
    }

    // this is only used in restore
    private animateSymbolsDuringRestore(data: number[][]): void {
        for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            for (let j: number = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                const reelsSymName: string = Game.model.initData.reelsAfterAvalanche[i][j],
                    symbol: Symbol = Game.reelsView.reelModels[i].getSymbolAt(j, reelsSymName);
                if (data[i][j] === 2 && !symbol.isSplit) {
                    symbol.splitSymbol().totalProgress(1);
                }

                if (symbol && symbol.name === "GE") {
                    symbol.getStateAnimationWithoutTL(SymbolStates.NORMAL, 5, true);
                }
            }
        }
    }

    private static showMaxExplodePatternAfterExpansion(addReelsToRight: boolean, addRowsInBottom: boolean, prevReelsNum: number, prevRowsNum: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1)
        });

        if (!(prevReelsNum === Game.TOTAL_REELS_NUM && prevRowsNum === Game.TOTAL_REELS_NUM)) {
            if (Game.ACTIVE_REELS_NUM > prevReelsNum) {
                if (addReelsToRight) {
                    for (let i = prevReelsNum - 1; i < Game.ACTIVE_REELS_NUM; i++) {
                        for (let j = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                            if (i === prevReelsNum - 1) {
                                tl.add(Game.showExplodePatternSparkOnSymbols(i, j, j + 1), 0);
                                continue;
                            }
                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(i, j, j + 1),
                                Game.showExplodePatternSparkOnSymbols(i, j, j + 1)
                            ], 0);
                        }
                    }
                } else {
                    for (let i = 0; i < Game.ACTIVE_REELS_NUM - prevReelsNum + 1; i++) {
                        for (let j = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                            if (i === Game.ACTIVE_REELS_NUM - prevReelsNum) {
                                tl.add(Game.showExplodePatternSparkOnSymbols(i, j, j + 1), 0);
                                continue;
                            }
                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(i, j, j + 1),
                                Game.showExplodePatternSparkOnSymbols(i, j, j + 1)
                            ], 0);
                        }
                    }
                }
            }

            if (Game.ACTIVE_ROWS_NUM > prevRowsNum) {
                if (addRowsInBottom) {
                    for (let i = prevRowsNum - 1; i < Game.ACTIVE_ROWS_NUM; i++) {
                        for (let j = 0; j < Game.ACTIVE_REELS_NUM; j++) {
                            if (i === prevRowsNum - 1) {
                                tl.add(Game.showExplodePatternSparkOnSymbols(j, i, i + 1), 0);
                                continue;
                            }
                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(j, i, i + 1),
                                Game.showExplodePatternSparkOnSymbols(j, i, i + 1)
                            ], 0);
                        }
                    }
                } else {
                    for (let i = 0; i < Game.ACTIVE_ROWS_NUM - prevRowsNum + 1; i++) {
                        for (let j = 0; j < Game.ACTIVE_REELS_NUM; j++) {
                            if (i === Game.ACTIVE_ROWS_NUM - prevRowsNum) {
                                tl.add(Game.showExplodePatternSparkOnSymbols(j, i, i + 1), 0);
                                continue;
                            }
                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(j, i, i + 1),
                                Game.showExplodePatternSparkOnSymbols(j, i, i + 1)
                            ], 0);
                        }
                    }
                }
            }
        }

        return tl;
    }

    private createViewsAndComponents(): void {
        // Non views
        Game.soundPlayer = new Sound();

        // Static views
        Game.backgroundElements = new BackgroundElements();
        Game.gameLogo = new GameLogo();
        Game.multiplier = new Multiplier();
        Game.monsterReelsView = new MonsterReelsView();
        Game.collectAreaHighlightView = new CollectAreaHighlightView();
        Game.reelsView = new ReelsView();
        Game.reelCorners = new ReelCorners();
        Game.symbolFloors = new SymbolFloors();
        Game.countUp = new CountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 50,
            tint: 0xffffff
        }, 650, "countUp");
        Game.wall = new Wall();
        Game.overlaysForSymbols = new OverlaysForSymbols();
        Game.featureIntro = new FeatureIntro();
        Game.featureOutro = new FeatureOutro();
        Game.scatterInventory = new ScatterInventory();
        Game.bigWin = new BigWin();
        Game.blackBg = new BlackBg();
        Game.mxCelebration = new MaxWinCelebration();

        NolimitApplication.addLayerAt("Speedster", new Speedster(), 0);
        NolimitApplication.addLayerAt("SpinePreviewer", new SpinePreviewer(Game.assetLoader));
        NolimitApplication.addLayerAt(Game.countUp.name, Game.countUp, 0);
        NolimitApplication.addLayerAt(Game.mxCelebration.name, Game.mxCelebration, 0);
        NolimitApplication.addLayerAt(Game.featureOutro.name, Game.featureOutro, 0);
        NolimitApplication.addLayerAt(Game.featureIntro.name, Game.featureIntro, 0);
        NolimitApplication.addLayerAt(Game.bigWin.name, Game.bigWin, 0);
        NolimitApplication.addLayerAt(Game.blackBg.name, Game.blackBg, 0);
        NolimitApplication.addLayerAt(Game.overlaysForSymbols.name, Game.overlaysForSymbols, 0);
        NolimitApplication.addLayerAt(Game.multiplier.name, Game.multiplier, 0);
        NolimitApplication.addLayerAt(Game.scatterInventory.name, Game.scatterInventory, 0);
        NolimitApplication.addLayerAt(Game.monsterReelsView.name, Game.monsterReelsView, 0);
        NolimitApplication.addLayerAt(Game.reelCorners.name, Game.reelCorners, 0);
        NolimitApplication.addLayerAt(Game.wall.name, Game.wall, 0);
        NolimitApplication.addLayerAt(Game.reelsView.name, Game.reelsView, 0);
        NolimitApplication.addLayerAt(Game.collectAreaHighlightView.name, Game.collectAreaHighlightView, 0);
        NolimitApplication.addLayerAt(Game.symbolFloors.name, Game.symbolFloors, 0);
        NolimitApplication.addLayerAt(Game.gameLogo.name, Game.gameLogo, 0);
        NolimitApplication.addLayerAt(Game.backgroundElements.name, Game.backgroundElements, 0);
    }
}